/**
 * SBAViewerEmbed — Thin wrapper around `RegulatoryViewerEmbed` bound
 * to the SBA family's API client.
 *
 * Pre-refactor this file was a 679-line copy of viewer logic.  All
 * of that has been moved to `RegulatoryViewerEmbed.tsx`, which now
 * serves SBA, EPAR, and UK PIL from one source.  Pre-existing
 * imports (`SBAViewerHandle`, default-export component) keep working
 * because we re-export the type and forward the default-export.
 */
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { ChunkMetadata, TOCEntry } from '../../types/viewer';
import { sbaApi } from '../../api/createViewerApi';
import RegulatoryViewerEmbed, {
  type ChunkAnchor,
  type RegulatoryViewerHandle,
} from './RegulatoryViewerEmbed';

// Re-export the handle type under its original name so existing
// `import type { SBAViewerHandle } from '.../SBAViewerEmbed'` calls
// keep type-checking.
export type SBAViewerHandle = RegulatoryViewerHandle;

interface SBAViewerEmbedProps {
  docId: number;
  highlightChunkIds?: number[];
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /** page_num -> anchor for a chunk-free (progressive-read) citation.
   *  Forwarded verbatim to RegulatoryViewerEmbed via the props spread. */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  highlightTerm?: string | null;
  highlightTerms?: string[] | null;
  highlightColors?: (string | undefined)[] | null;
  landingPage?: number | undefined;
  onChunkClick?: (chunk: ChunkMetadata) => void;
  visibleSectionNumbers?: string[] | undefined;
  onTocLoaded?: ((entries: TOCEntry[]) => void) | undefined;
}

const SBAViewerEmbed = forwardRef<SBAViewerHandle, SBAViewerEmbedProps>(
  function SBAViewerEmbed(props, ref) {
    const innerRef = useRef<RegulatoryViewerHandle>(null);
    useImperativeHandle(ref, () => ({
      scrollToPage: (n) => innerRef.current?.scrollToPage(n),
      highlightChunks: (ids) => innerRef.current?.highlightChunks(ids),
      scrollToSection: (s, opts) => innerRef.current?.scrollToSection(s, opts) ?? false,
    }));
    return (
      <RegulatoryViewerEmbed
        ref={innerRef}
        viewerApi={sbaApi}
        {...props}
      />
    );
  },
);

export default SBAViewerEmbed;
